import type { PackagesFormStateType } from '@/components/Packages/types'
import { PKG_SEARCH_SESSION_RECOVERY_KEY } from '@/constants/pkg-search-recovery'

import { buildVacationPackagesFormFromTripsInitialValues } from './build-vacation-packages-form-from-trips-initial-values'

/** SETI: when `containsXSell` is true on the recovery payload, only persist restore/redirect in VARIANT. */
export const PKG_XSELL_ERROR_PERSIST_SEARCH_CRITERIA_SETI = 'PKG_XSELL_ERROR_PERSIST_SEARCH_CRITERIA'

/** SETI / fireImpression tag gating the package hotel-listings error redirect + search-criteria persistence. */
export const PKG_HOTEL_LISTINGS_ERROR_PERSIST_SEARCH_CRITERIA_SETI =
  'PKG_HOTEL_LISTINGS_ERROR_PERSIST_SEARCH_CRITERIA'

export type PkgSessionRecoveryPayload = {
  isAdvancedSearch?: boolean
  containsXSell?: boolean
  tripsSearchForm?: Record<string, unknown>
}

export type ConsumePkgSessionRecoveryForLandingFormParams = {
  seti: Record<string, string> | undefined
}

export type ConsumePkgSessionRecoveryForLandingFormResult = {
  formState: PackagesFormStateType | null
  restored: boolean
}

export function readPkgSessionRecoveryFromStorage(): PkgSessionRecoveryPayload | null {
  if (globalThis.window === undefined) {
    return null
  }
  try {
    const raw = sessionStorage.getItem(PKG_SEARCH_SESSION_RECOVERY_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as PkgSessionRecoveryPayload
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function clearPkgSessionRecoveryFromStorage(): void {
  if (globalThis.window === undefined) {
    return
  }
  try {
    sessionStorage.removeItem(PKG_SEARCH_SESSION_RECOVERY_KEY)
  } catch {
    // noop — storage unavailable
  }
}

/** Non–xsell recovery is always allowed; xsell persistence/restores require PKG_XSELL_ERROR_PERSIST_SEARCH_CRITERIA === VARIANT and containsXSell === true. */
export function shouldApplyPkgRecoveryForXsell(
  payload: PkgSessionRecoveryPayload | null,
  seti: Record<string, string> | undefined,
): boolean {
  if (payload?.containsXSell !== true) {
    return true
  }
  return seti?.[PKG_XSELL_ERROR_PERSIST_SEARCH_CRITERIA_SETI] === 'VARIANT'
}

function normalizeRecoveredTravelers(
  travelers: PackagesFormStateType['travelers'],
): PackagesFormStateType['travelers'] {
  const children = travelers.children as number | unknown[]
  return {
    adults: Number(travelers.adults) || 2,
    children: Array.isArray(children) ? children.length : Number(children) || 0,
    lapInfants: Number(travelers.lapInfants) || 0,
    rooms: Number(travelers.rooms) || 1,
    childrenAges:
      travelers.childrenAges && typeof travelers.childrenAges === 'object' ? travelers.childrenAges : {},
  }
}

/**
 * Reads pkgSearchRecovery for the vacationpackages landing form.
 * Restores only when isAdvancedSearch === false and tripsSearchForm is present.
 * Leaves storage in place after a successful restore so React Strict Mode remounts
 * (and same-tab back/refresh with the same error handoff) can re-read it.
 * Clears storage only when xsell persist SETI blocks apply.
 */
export function consumePkgSessionRecoveryForLandingForm({
  seti,
}: ConsumePkgSessionRecoveryForLandingFormParams): ConsumePkgSessionRecoveryForLandingFormResult {
  try {
    const payload = readPkgSessionRecoveryFromStorage()
    if (!payload) {
      return { formState: null, restored: false }
    }

    if (!shouldApplyPkgRecoveryForXsell(payload, seti)) {
      clearPkgSessionRecoveryFromStorage()
      return { formState: null, restored: false }
    }

    if (
      payload.isAdvancedSearch !== false ||
      !payload.tripsSearchForm ||
      typeof payload.tripsSearchForm !== 'object'
    ) {
      // Keep storage for advanced-search redirect.
      return { formState: null, restored: false }
    }

    const parsed = buildVacationPackagesFormFromTripsInitialValues(payload.tripsSearchForm)
    parsed.travelers = normalizeRecoveredTravelers(parsed.travelers)
    return { formState: parsed, restored: true }
  } catch {
    return { formState: null, restored: false }
  }
}
